import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type SelectOption,
} from '../../ui';
import type { Sprint } from '../../features/sprintboard/models/types';
import {
  CHORUS_EXAMPLE_LINK,
  blankPrePlanDraft,
  validatePrePlanDraft,
} from '../../features/preplanning/prePlanModel';
import type { PrePlanDraft } from '../../features/preplanning/types';
import type { TaskTemplateSummary } from '../../features/taskTemplates/types';

export interface PrePlanEditorModalProps {
  open: boolean;
  /** Draft to edit; when omitted the modal opens a blank create form. */
  initial?: PrePlanDraft;
  /** Sprints available in context, offered in the optional sprint selector. */
  sprints: Sprint[];
  /**
   * Task templates offered in the optional "Start from template" picker. When
   * omitted/empty the picker is hidden.
   */
  templates?: TaskTemplateSummary[];
  /** Loads a template's Markdown content by id (used to prefill). */
  loadTemplateContent?: (id: string) => Promise<string>;
  /** Whether a save is in flight. */
  saving?: boolean;
  onClose: () => void;
  onSave: (draft: PrePlanDraft) => void;
}

/**
 * Create / edit modal for a pre-plan. Collects title, month, an optional sprint,
 * the Markdown content, and an optional external doc link (validated http/https,
 * placeholder-seeded with the Chorus example). Reuses the accessible {@link Modal}.
 *
 * When {@link PrePlanEditorModalProps.templates} are supplied, a "Start from
 * template" picker prefills the Markdown content from the chosen task template —
 * non-destructively: it fills straight in when the content is empty, and asks
 * for confirmation before overwriting existing content.
 */
export function PrePlanEditorModal({
  open,
  initial,
  sprints,
  templates = [],
  loadTemplateContent,
  saving = false,
  onClose,
  onSave,
}: PrePlanEditorModalProps) {
  const isEdit = initial !== undefined;
  const [draft, setDraft] = useState<PrePlanDraft>(
    () => initial ?? blankPrePlanDraft(),
  );
  const [problems, setProblems] = useState<string[]>([]);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  // Reset the form whenever the modal (re)opens or its subject changes.
  useEffect(() => {
    if (open) {
      setDraft(initial ?? blankPrePlanDraft());
      setProblems([]);
    }
  }, [open, initial]);

  const sprintOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'No sprint' },
      ...sprints.map((s) => ({ value: s.id, label: s.name })),
    ],
    [sprints],
  );

  const templateOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'Start from template…' },
      ...templates.map((t) => ({
        value: t.id,
        label: t.isDefault ? `${t.name} (default)` : t.name,
      })),
    ],
    [templates],
  );

  const patch = (fields: Partial<PrePlanDraft>) =>
    setDraft((prev) => ({ ...prev, ...fields }));

  const onSprintChange = (sprintId: string) => {
    if (!sprintId) {
      patch({ sprintId: undefined, sprintName: undefined });
      return;
    }
    const sprint = sprints.find((s) => s.id === sprintId);
    patch({ sprintId, sprintName: sprint?.name });
  };

  const onPickTemplate = async (templateId: string) => {
    if (!templateId || !loadTemplateContent) return;
    // Non-destructive: only overwrite non-empty content after confirmation.
    if (
      draft.content.trim() &&
      !window.confirm(
        'Replace the current content with this template? Your current content will be overwritten.',
      )
    ) {
      return;
    }
    setApplyingTemplate(true);
    try {
      const content = await loadTemplateContent(templateId);
      if (content) patch({ content });
    } finally {
      setApplyingTemplate(false);
    }
  };

  const submit = () => {
    const found = validatePrePlanDraft(draft);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    onSave(draft);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEdit ? 'Edit pre-plan' : 'New pre-plan'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            {isEdit ? 'Save changes' : 'Create pre-plan'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {problems.length > 0 && (
          <ul
            className="space-y-1 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
            role="alert"
          >
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}

        <Input
          label="Title"
          value={draft.title}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder="e.g. Sprint 24 — Checkout pre-plan"
          autoFocus
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Month"
            type="month"
            value={draft.month}
            onChange={(e) => patch({ month: e.target.value })}
            hint="Groups the pre-plan under a month."
          />
          <Select
            label="Sprint (optional)"
            value={draft.sprintId ?? ''}
            options={sprintOptions}
            onChange={(e) => onSprintChange(e.target.value)}
          />
        </div>

        {templates.length > 0 && loadTemplateContent && (
          <Select
            label="Start from template (optional)"
            value=""
            options={templateOptions}
            disabled={applyingTemplate}
            onChange={(e) => void onPickTemplate(e.target.value)}
          />
        )}

        <Textarea
          label="Content (Markdown)"
          value={draft.content}
          onChange={(e) => patch({ content: e.target.value })}
          rows={12}
          placeholder={'# Pre-plan\n\n- Goals\n- Scope\n- Risks\n\n| Item | Owner |\n| --- | --- |\n| … | … |'}
          hint="Rendered inline with headings, links, tables, lists and code."
        />

        <Input
          label="External doc link (optional)"
          type="url"
          value={draft.externalLink ?? ''}
          onChange={(e) => patch({ externalLink: e.target.value })}
          placeholder={CHORUS_EXAMPLE_LINK}
          hint="A canonical planning doc (e.g. Chorus). Shown as a prominent open-in link."
        />
      </div>
    </Modal>
  );
}

export default PrePlanEditorModal;
